import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";

/**
 * Toggles the checkbox selection state for a specific row in the Oncology Submission table.
 *
 * @param {string} id - The unique identifier of the row whose checkbox should be toggled
 * @param {React.Dispatch<React.SetStateAction<OncologySubmissionTableDataModel[]>>} setTableData - The state setter function for updating the table data
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setHasSelectedRows - The state setter for tracking if any row is selected
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setAreAllRowsSelected - The state setter for tracking if all rows are selected
 *
 * @returns {void}
 *
 * @example
 * toggleCheckboxSelection("order-123", setTableData, setHasSelectedRows, setAreAllRowsSelected);
 */
export const toggleCheckboxSelection = (
  id: string,
  setTableData: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableDataModel[]>
  >,
  setHasSelectedRows: React.Dispatch<React.SetStateAction<boolean>>,
  setAreAllRowsSelected?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setTableData((prevData) => {
    const updatedData = prevData.map((item) => {
      if (item.batch?.id === id) {
        return {
          ...item,
          batch: {
            ...item.batch,
            isChecked: !item.batch.isChecked,
          },
        };
      }
      return item;
    });

    // Check if any checkbox is selected
    const hasSelectedItems = updatedData.some((item) => item.batch?.isChecked);
    setHasSelectedRows(hasSelectedItems);

    // Check if all checkboxes are selected
    if (setAreAllRowsSelected) {
      const allSelected =
        updatedData.length > 0 &&
        updatedData.every((item) => item.batch?.isChecked);
      setAreAllRowsSelected(allSelected);
    }

    return updatedData;
  });
};

/**
 * Gets all selected rows from the table data.
 *
 * @param {OncologySubmissionTableDataModel[]} tableData - The table data array
 * @returns {OncologySubmissionTableDataModel[]} - Array of selected rows
 */
export const getSelectedRows = (
  tableData: OncologySubmissionTableDataModel[],
): OncologySubmissionTableDataModel[] => {
  return tableData.filter((item) => item.batch?.isChecked);
};

/**
 * Selects all rows in the table.
 *
 * @param {React.Dispatch<React.SetStateAction<OncologySubmissionTableDataModel[]>>} setTableData - The state setter function for updating the table data
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setHasSelectedRows - The state setter for tracking if any row is selected
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setAreAllRowsSelected - The state setter for tracking if all rows are selected
 */
export const selectAllRows = (
  setTableData: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableDataModel[]>
  >,
  setHasSelectedRows: React.Dispatch<React.SetStateAction<boolean>>,
  setAreAllRowsSelected?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setTableData((prevData) => {
    const updatedData = prevData.map((item) => ({
      ...item,
      batch: {
        ...item.batch,
        isChecked: true,
      },
    }));
    setHasSelectedRows(true);
    if (setAreAllRowsSelected) {
      setAreAllRowsSelected(true);
    }
    return updatedData;
  });
};

/**
 * Deselects all rows in the table.
 *
 * @param {React.Dispatch<React.SetStateAction<OncologySubmissionTableDataModel[]>>} setTableData - The state setter function for updating the table data
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setHasSelectedRows - The state setter for tracking if any row is selected
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setAreAllRowsSelected - The state setter for tracking if all rows are selected
 */
export const deselectAllRows = (
  setTableData: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableDataModel[]>
  >,
  setHasSelectedRows: React.Dispatch<React.SetStateAction<boolean>>,
  setAreAllRowsSelected?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setTableData((prevData) => {
    const updatedData = prevData.map((item) => ({
      ...item,
      batch: {
        ...item.batch,
        isChecked: false,
      },
    }));
    setHasSelectedRows(false);
    if (setAreAllRowsSelected) {
      setAreAllRowsSelected(false);
    }
    return updatedData;
  });
};

/**
 * Selects only the rows on the current page.
 *
 * @param {React.Dispatch<React.SetStateAction<OncologySubmissionTableDataModel[]>>} setTableData - The state setter function for updating the table data
 * @param {number} startIndex - The start index of the current page
 * @param {number} endIndex - The end index of the current page
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setAreAllRowsOnPageSelected - The state setter for tracking if all rows on page are selected
 */
export const selectRowsOnCurrentPage = (
  setTableData: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableDataModel[]>
  >,
  startIndex: number,
  endIndex: number,
  setAreAllRowsOnPageSelected?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setTableData((prevData) => {
    const updatedData = prevData.map((item, index) => {
      // Only select items within the current page range
      if (index >= startIndex && index <= endIndex) {
        return {
          ...item,
          batch: {
            ...item.batch,
            isChecked: true,
          },
        };
      }
      return item;
    });

    if (setAreAllRowsOnPageSelected) {
      setAreAllRowsOnPageSelected(true);
    }

    return updatedData;
  });
};

/**
 * Deselects only the rows on the current page.
 *
 * @param {React.Dispatch<React.SetStateAction<OncologySubmissionTableDataModel[]>>} setTableData - The state setter function for updating the table data
 * @param {number} startIndex - The start index of the current page
 * @param {number} endIndex - The end index of the current page
 * @param {React.Dispatch<React.SetStateAction<boolean>>} setAreAllRowsOnPageSelected - The state setter for tracking if all rows on page are selected
 */
export const deselectRowsOnCurrentPage = (
  setTableData: React.Dispatch<
    React.SetStateAction<OncologySubmissionTableDataModel[]>
  >,
  startIndex: number,
  endIndex: number,
  setAreAllRowsOnPageSelected?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setTableData((prevData) => {
    const updatedData = prevData.map((item, index) => {
      // Only deselect items within the current page range
      if (index >= startIndex && index <= endIndex) {
        return {
          ...item,
          batch: {
            ...item.batch,
            isChecked: false,
          },
        };
      }
      return item;
    });

    if (setAreAllRowsOnPageSelected) {
      setAreAllRowsOnPageSelected(false);
    }

    return updatedData;
  });
};

/**
 * Checks if all rows on the current page are selected.
 *
 * @param {OncologySubmissionTableDataModel[]} tableData - The table data array
 * @param {number} startIndex - The start index of the current page
 * @param {number} endIndex - The end index of the current page
 * @returns {boolean} - True if all rows on the current page are selected
 */
export const areAllRowsOnPageSelected = (
  tableData: OncologySubmissionTableDataModel[],
  startIndex: number,
  endIndex: number,
): boolean => {
  const pageItems = tableData.slice(startIndex, endIndex + 1);
  return (
    pageItems.length > 0 && pageItems.every((item) => item.batch?.isChecked)
  );
};
